/**
 * ImageWithLQIP Component - Phase 4b: Progressive Image Loading
 *
 * Renders images with LQIP (Low Quality Image Placeholder):
 * - Displays thumbnail/dominant color immediately
 * - Fades in full-resolution image as it loads
 * - Fully responsive with srcSet support
 * - Graceful error handling
 *
 * Usage:
 * <ImageWithLQIP
 *   src={imageUrl}
 *   alt="question visual"
 *   thumbnailSrc={thumbnailUrl}
 *   dominantColor="#cccccc"
 * />
 */

import React, { useState, useCallback, useMemo } from 'react';
import { imageOptimization } from '../../services/imageOptimizationService';
import './ImageWithLQIP.css';

interface ImageWithLQIPProps {
  src: string; // Full-resolution image URL
  alt: string;
  thumbnailSrc?: string; // Low-quality placeholder thumbnail
  dominantColor?: string; // Hex color for background while loading
  width?: number;
  height?: number;
  className?: string;
  /**
   * How the image should be fit into the LQIP aspect-ratio container.
   * `cover` matches the current implementation but can crop.
   * `contain` ensures the full sign is visible (used by AI exam).
   */
  objectFit?: 'cover' | 'contain';
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Image component with LQIP (Low Quality Image Placeholder)
 * - Shows thumbnail/color instantly
 * - Loads full-res in background
 * - Smooth fade transition
 */
export const ImageWithLQIP: React.FC<ImageWithLQIPProps> = ({
  src,
  alt,
  thumbnailSrc,
  dominantColor,
  width = 1536,
  height = 1024,
  className = '',
  objectFit = 'cover',
  onLoad,
  onError
}) => {
  const [isFullResLoaded, setIsFullResLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Generate responsive srcSet
  const srcSet = useMemo(() => {
    return imageOptimization.generateSrcSet(src, thumbnailSrc);
  }, [src, thumbnailSrc]);

  // Generate sizes attribute
  const sizes = useMemo(() => {
    return imageOptimization.generateSizes();
  }, []);

  const handleFullResLoad = useCallback(() => {
    setIsFullResLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setImageError(true);
    onError?.();
  }, [onError]);

  // If image failed to load, don't render
  if (imageError) {
    return null;
  }

  const aspectRatio = width / height;
  const containerStyle = {
    backgroundColor: dominantColor || '#f0f0f0',
    aspectRatio: `${aspectRatio}`,
    // Used by CSS to avoid cropping in contexts that require full visibility.
    ['--image-with-lqip-object-fit' as unknown as string]: objectFit,
  };

  return (
    <div
      className={`image-with-lqip ${className}`}
      style={containerStyle}
    >
      {/* Thumbnail/LQIP (shown immediately) */}
      {thumbnailSrc && (
        <img
          src={thumbnailSrc}
          alt={alt}
          className="image-lqip-thumbnail"
          loading="eager"
          decoding="async"
          aria-hidden="true"
          onError={() => {
            // Silently fail for thumbnail
          }}
        />
      )}

      {/* Full-resolution image (fades in when loaded) */}
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        className={`image-lqip-full ${isFullResLoaded ? 'loaded' : 'loading'}`}
        loading="eager"
        decoding="async"
        onLoad={handleFullResLoad}
        onError={handleError}
      />
    </div>
  );
};

/**
 * Wrapper hook to auto-generate image metadata
 */
export function useImageMetadata(
  imageUrl: string,
  width?: number,
  height?: number
) {
  const [metadata, setMetadata] = React.useState<{
    thumbnailUrl?: string;
    dominantColor: string;
  } | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    let isMounted = true;

    const loadMetadata = async () => {
      try {
        const result = await imageOptimization.getImageMetadata(
          imageUrl,
          width,
          height
        );
        if (isMounted) {
          setMetadata({
            thumbnailUrl: result.thumbnailUrl,
            dominantColor: result.dominantColor
          });
        }
      } catch (error) {
        console.error('Error loading image metadata:', error);
        if (isMounted) {
          setMetadata({
            dominantColor: '#cccccc'
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMetadata();

    return () => {
      isMounted = false;
    };
  }, [imageUrl, width, height]);

  return { metadata, isLoading };
}

export default ImageWithLQIP;
